/**
 * Artwork Profile — contextual side panel (#15 discoverability).
 *
 * Shown when a Frozen Artwork is selected on the Grid (instead of the generic
 * Parcel Profile). All data is backend-authoritative (`artworkApi.getArtwork`);
 * nothing is fabricated. Owner (wagmi address === artwork.owner) sees an inline
 * Edit Placement action that opens the authoritative PlacementEditor.
 */
import { useEffect, useState } from "react";
import { useAccount } from "wagmi";
import { artworkApi, ArtworkApiError, type ArtworkPublic } from "../../lib/artwork-api";
import { FrozenArtworkRender } from "../../viewer/FrozenArtworkRender";
import { OwnerModerationBanner } from "../moderation/OwnerModerationBanner";
import { PlacementControls } from "./PlacementControls";

function shortWallet(w: string): string {
  return w && w.length > 12 ? `${w.slice(0, 6)}…${w.slice(-4)}` : w;
}

function Row({ label, value, testid, mono }: { label: string; value: React.ReactNode; testid?: string; mono?: boolean }) {
  return (
    <div className="flex items-start justify-between gap-3 border-b border-white/5 py-1.5">
      <span className="font-mono text-[9px] uppercase tracking-[0.16em] text-white/40">{label}</span>
      <span data-testid={testid} className={`text-right text-xs text-white/80 ${mono ? "font-mono break-all" : ""}`}>{value}</span>
    </div>
  );
}

export function ArtworkProfilePanel({
  artworkId, parcelLabel, onBackToParcel,
}: { artworkId: string; parcelLabel?: string | null; onBackToParcel?: () => void }) {
  const { address } = useAccount();
  const [art, setArt] = useState<ArtworkPublic | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    const c = new AbortController();
    setError(null); setArt(null);
    artworkApi.getArtwork(artworkId, c.signal)
      .then(setArt)
      .catch((e) => { if (!c.signal.aborted) setError(e instanceof ArtworkApiError ? e.message : "Failed to load artwork"); });
    return () => c.abort();
  }, [artworkId, tick]);

  const isOwner = !!(address && art && address.toLowerCase() === art.owner.toLowerCase());

  // auto-open in-context edit when arriving from "Edit Placement on Grid"
  useEffect(() => {
    if (art && isOwner && window.location.hash.includes("edit=1")) setEditing(true);
  }, [art, isOwner]);

  const pl = art?.placement;
  const rotDeg = pl ? ((pl.rotation_millideg ?? pl.rotation * 1000) / 1000) : 0;

  return (
    <div data-testid="artwork-profile-panel" className="flex flex-col gap-3">
      {/* breadcrumb / back */}
      <button
        data-testid="artwork-breadcrumb-back"
        onClick={onBackToParcel}
        className="inline-flex items-center gap-2 self-start rounded-full border border-white/15 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.14em] text-white/60 hover:text-white"
      >
        ← {parcelLabel ? `Back to ${parcelLabel}` : "Back to Parcel"}
      </button>

      <OwnerModerationBanner targetType="artwork" targetId={artworkId} enabled={isOwner} />

      {error && <div data-testid="artwork-profile-error" className="text-sm text-red-300">{error}</div>}
      {!art && !error && <div className="p-4 text-center text-white/40 text-sm">Loading artwork…</div>}

      {art && (
        <>
          <div className="flex items-center gap-3">
            {art.content && (
              <FrozenArtworkRender testid="artwork-profile-thumb" content={art.content} width={art.width} height={art.height} maxPx={64} />
            )}
            <div className="min-w-0">
              <h3 data-testid="artwork-profile-title" className="truncate text-base font-semibold text-white">{art.title || "Untitled"}</h3>
              <span data-testid="artwork-profile-badge" className="mt-1 inline-flex rounded-full border border-teal-300/40 bg-teal-400/10 px-2 py-0.5 font-mono text-[9px] uppercase tracking-[0.14em] text-teal-200">Frozen Artwork</span>
            </div>
          </div>

          <div className="rounded-xl border border-white/10 bg-white/[0.02] p-3">
            <Row label="Creator" value={shortWallet(art.creator)} testid="artwork-profile-creator" mono />
            <Row label="Owner" testid="artwork-profile-owner" value={
              <span className="inline-flex items-center justify-end gap-1.5">
                {art.owner.toLowerCase() !== art.creator.toLowerCase() && (
                  <span data-testid="artwork-profile-acquired" className="rounded border border-amber-300/30 bg-amber-400/10 px-1 py-0.5 font-mono text-[8px] uppercase tracking-wider text-amber-200">Acquired</span>
                )}
                <span className="font-mono break-all">{shortWallet(art.owner)}</span>
              </span>
            } />
            <Row label="Frozen" value={new Date(art.frozen_at).toLocaleDateString()} />
            <Row label="Size" value={`${art.width} × ${art.height}`} />
            <Row label="Footprint" value={`${art.footprint} cells`} />
            <Row label="Royalty" value={`${art.royalty_percent}%`} />
            <Row label="Hash" value={art.content_hash.slice(0, 18) + "…"} mono />
            {pl && <Row label="Placement" testid="artwork-profile-placement" value={`(${pl.anchor_x}, ${pl.anchor_y}) · ${rotDeg}°${pl.background ? ` · bg` : ""}`} />}
            {pl && <Row label="Opacity" value={`${Math.round((pl.empty_opacity_bps ?? 0) / 100)}%`} />}
          </div>

          <div className="flex flex-wrap gap-2">
            <a data-testid="artwork-profile-open" href={`#artwork/${art.artwork_id}`}
              className="rounded-full border border-white/20 px-3 py-1.5 text-xs text-white/80 no-underline hover:border-teal-300/40">
              View / Provenance
            </a>
            {isOwner && (
              <button data-testid="artwork-profile-edit-placement" onClick={() => setEditing(true)}
                className="rounded-full bg-teal-400 px-3 py-1.5 text-xs font-semibold text-black hover:bg-teal-300">
                Edit Placement
              </button>
            )}
          </div>
        </>
      )}

      {editing && (
        <PlacementControls
          artworkId={artworkId}
          onClose={() => setEditing(false)}
          onCommitted={() => { setEditing(false); setTick((t) => t + 1); }}
        />
      )}
    </div>
  );
}
